import React, { useState, useEffect } from 'react';
import './App.css';
import { useAuth0 } from '@auth0/auth0-react';
import LoginButton from './components/LoginButton';
import LogoutButton from './components/LogoutButton';
import ProfilePage from './components/ProfilePage';
import LeaderboardPage from './components/LeaderboardPage';
import { getWebSocketUrl, getAuth0AccessToken } from './utils/api';
import WebSocketService from './services/WebSocketService';
import { v4 as uuidv4 } from 'uuid';
import { playClickSound, playTabSound } from './utils/sounds';

const WEBSOCKET_URL = getWebSocketUrl();
console.log('WEBSOCKET_URL = ' + WEBSOCKET_URL);

function MainMenu({ onSelectMode, onUpdateSettings, viewMode, preferredColor }) {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = useState(() => {
    // Restore last active tab from sessionStorage, default to 'home'
    return sessionStorage.getItem('blockRivalsActiveTab') || 'home';
  });
  const [hasActiveGame, setHasActiveGame] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  const handleSinglePlayer = () => {
    playClickSound();
    onSelectMode('single');
  };

  const handleMultiplayer = () => {
    playClickSound();
    onSelectMode('multiplayer', WEBSOCKET_URL);
  };

  const handleRanked = () => {
    playClickSound();
    if (!isAuthenticated) {
      alert('Please log in to play ranked mode');
      return;
    }
    onSelectMode('ranked', WEBSOCKET_URL);
  };

  const handleViewModeChange = (mode) => {
    playClickSound();
    onUpdateSettings(mode, preferredColor);
  };

  const handleColorChange = (color) => {
    playClickSound();
    onUpdateSettings(viewMode, color);
  };

  const handleReconnect = () => {
    playClickSound();
    // Navigate to multiplayer mode with autoReconnect flag
    onSelectMode('multiplayer', WEBSOCKET_URL, true);
  };

  const handleTabChange = (tab) => {
    playTabSound();
    setActiveTab(tab);
    // Persist the active tab to sessionStorage
    sessionStorage.setItem('blockRivalsActiveTab', tab);
  };

  // Update display name when authentication status changes or tab changes
  useEffect(() => {
    const loadUsername = async () => {
      if (isAuthenticated && user) {
        const savedUsername = localStorage.getItem('blockRivalsUsername');
        if (savedUsername) {
          // If we have a saved username, use it immediately
          setDisplayName(savedUsername);
          setIsLoadingProfile(false);
        } else {
          // If no saved username yet, fetch it from the server
          setIsLoadingProfile(true);
          setDisplayName('');

          try {
            const authToken = await getAuth0AccessToken(getAccessTokenSilently);
            const wsUrl = getWebSocketUrl();

            const handleIdentified = (message) => {
              const { body } = message;
              if (body && body.accepted && body.guest === false && body.nickname) {
                setDisplayName(body.nickname);
                localStorage.setItem('blockRivalsUsername', body.nickname);
              }
              WebSocketService.off('Identified', handleIdentified);
              WebSocketService.disconnect();
              setIsLoadingProfile(false);
            };

            WebSocketService.on('Identified', handleIdentified);
            await WebSocketService.connect(wsUrl);
            WebSocketService.identify(user.sub, '', authToken);
          } catch (error) {
            console.error('Failed to load username:', error);
            setIsLoadingProfile(false);
          }
        }
      } else {
        setDisplayName('');
        setIsLoadingProfile(false);
      }
    };

    loadUsername();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Listen for username updates from ProfilePage
  useEffect(() => {
    const handleUsernameUpdate = (event) => {
      setDisplayName(event.detail);
      setIsLoadingProfile(false);
    };

    window.addEventListener('usernameUpdated', handleUsernameUpdate);
    return () => window.removeEventListener('usernameUpdated', handleUsernameUpdate);
  }, []);

  // Check for active game session on mount
  useEffect(() => {
    if (isLoading) return;

    const checkForActiveSession = async () => {
      setCheckingSession(true);

      try {
        // Get or create player ID
        let playerId;
        let authToken = null;

        if (isAuthenticated && user) {
          playerId = user.sub;
          try {
            authToken = await getAuth0AccessToken(getAccessTokenSilently);
          } catch (error) {
            console.error('Failed to get access token:', error);
          }
        } else {
          // Guest player - get persisted UUID
          playerId = localStorage.getItem('blockrivalsGuestPlayerId');
          if (!playerId) {
            playerId = uuidv4();
            localStorage.setItem('blockrivalsGuestPlayerId', playerId);
          }
        }

        // Connect to WebSocket
        await WebSocketService.connect(WEBSOCKET_URL);

        // Set up one-time listener for Identified response
        const handleIdentified = (message) => {
          console.log('Session check - Identified:', message);
          const { body } = message;

          console.log('  body.resume:', body.resume);
          console.log('  body.sessionId:', body.sessionId);
          console.log('  body.gameEnded:', body.gameEnded);

          // Check if there's a resumable session AND the game hasn't ended
          // The backend should not send resume=true for ended games, but check gameEnded field if available
          if (body.resume === true && body.sessionId && body.gameEnded !== true) {
            console.log('✅ Active game session found!');
            setHasActiveGame(true);
          } else {
            if (body.gameEnded === true) {
              console.log('❌ Game session exists but has ended - not showing reconnect');
            } else if (!body.resume) {
              console.log('❌ No resume flag from backend');
            } else if (!body.sessionId) {
              console.log('❌ No sessionId from backend');
            } else {
              console.log('❌ No active game session (unknown reason)');
            }
            setHasActiveGame(false);
          }

          // Clean up
          WebSocketService.off('Identified', handleIdentified);
          WebSocketService.disconnect();
          setCheckingSession(false);
        };

        WebSocketService.on('Identified', handleIdentified);

        // Send Identify message
        console.log('🔍 Sending Identify for session check:', { playerId, authToken: authToken ? '***' : null });
        WebSocketService.identify(playerId, '', authToken);

      } catch (error) {
        console.error('Error checking for active session:', error);
        setHasActiveGame(false);
        setCheckingSession(false);
      }
    };

    checkForActiveSession();
  }, [isLoading, isAuthenticated, user, getAccessTokenSilently]);

  if (isLoading) {
    return (
      <div className={viewMode === 'mobile' ? 'App mobile-view' : 'App'}>
        <h1>Block Rivals</h1>
        <div className="menu-container">
          <div className="menu-content">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={viewMode === 'mobile' ? 'App mobile-view' : 'App'}>
      <h1>Block Rivals</h1>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          onClick={() => handleTabChange('home')}
          className={`tab-btn ${activeTab === 'home' ? 'active' : ''}`}
        >
          Home
        </button>
        <button
          onClick={() => handleTabChange('profile')}
          className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
        >
          Profile
        </button>
        <button
          onClick={() => handleTabChange('leaderboard')}
          className={`tab-btn ${activeTab === 'leaderboard' ? 'active' : ''}`}
        >
          Leaderboard
        </button>
      </div>

      <div className="menu-container">
        <div className="menu-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <ProfilePage viewMode={viewMode} />
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <LeaderboardPage viewMode={viewMode} />
          )}

          {/* Home Tab */}
          {activeTab === 'home' && (
            <>
          {isAuthenticated && user && (
            <div className="user-info-section">
              <p className="welcome-text">
                {isLoadingProfile ? 'Loading profile...' : `Welcome, ${displayName}!`}
              </p>
              <LogoutButton />
            </div>
          )}

          {!isAuthenticated && (
            <div className="auth-section">
              <p className="auth-prompt">Log in to play ranked mode and track your progress</p>
              <LoginButton />
            </div>
          )}

          {hasActiveGame && (
            <div className="reconnect-section">
              <button onClick={handleReconnect} className="menu-btn reconnect-btn">
                🔄 Reconnect to Game in Progress
              </button>
            </div>
          )}

          <h2>Select Game Mode</h2>
          <div className="menu-buttons">
            <button onClick={handleSinglePlayer} className="menu-btn primary">
              <div className="button-content">
                <span className="button-text">Single Player</span>
                <span className="button-icon">👤</span>
              </div>
            </button>
            <button onClick={handleMultiplayer} className="menu-btn primary">
              <div className="button-content">
                <span className="button-text">Multiplayer</span>
                <span className="button-icon">👥</span>
              </div>
            </button>
            <div
              className="tooltip-wrapper"
              data-tooltip={!isAuthenticated ? 'Login Required' : ''}
            >
              <button
                onClick={handleRanked}
                className={`menu-btn ${isAuthenticated ? 'primary' : 'disabled'}`}
                disabled={!isAuthenticated}
              >
                <div className="button-content">
                  <span className="button-text">Ranked</span>
                  <span className="button-icon">⚔️</span>
                </div>
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3>Preferred Color</h3>
            <div className="menu-buttons color-selection">
              <button
                onClick={() => handleColorChange('blue')}
                className={`menu-btn color-btn ${preferredColor === 'blue' ? 'selected' : ''}`}
                style={{ backgroundColor: '#3b82f6', color: 'white' }}
              >
                Blue
              </button>
              <button
                onClick={() => handleColorChange('red')}
                className={`menu-btn color-btn ${preferredColor === 'red' ? 'selected' : ''}`}
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                Red
              </button>
              <button
                onClick={() => handleColorChange('orange')}
                className={`menu-btn color-btn ${preferredColor === 'orange' ? 'selected' : ''}`}
                style={{ backgroundColor: '#f97316', color: 'white' }}
              >
                Orange
              </button>
              <button
                onClick={() => handleColorChange('purple')}
                className={`menu-btn color-btn ${preferredColor === 'purple' ? 'selected' : ''}`}
                style={{ backgroundColor: '#a855f7', color: 'white' }}
              >
                Purple
              </button>
            </div>
          </div>
          <div className="game-info">
            <p><strong>How to Play:</strong></p>
            <ul>
              <li>Place all 21 unique pieces on the board</li>
              <li>First piece must touch your starting corner</li>
              <li>New pieces must connect corner-to-corner</li>
              <li>Pieces cannot share edges with your own color</li>
            </ul>
          </div>

          <div className="discord-section">
            <a
              href="https://discord.gg/Yyxt6CuX"
              target="_blank"
              rel="noopener noreferrer"
              className="discord-link"
            >
              <svg className="discord-icon" width="24" height="24" viewBox="0 0 127.14 96.36" fill="currentColor">
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
              </svg>
              <span>Join our community</span>
            </a>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MainMenu;
